import React from 'react';
import classNameBind from 'classnames/bind';
import get from 'lodash/get';
import Popover from '../../../Popover';
import Tooltip from '../../../Tooltip';
import Button from '../../../Button';
import ButtonGroup from '../../../ButtonGroup';
import SegmentPickerInner from '../SegmentPickerInner';
import styles from './SegmentButton.module.css';

const bindClasses = classNameBind.bind(styles);

const SegmentButton = ({
  slot,
  segment,
  items,
  isLoading,
  withAction,
  withInfo,
  canManage,
  compareMode,
  onItemSelected,
  onManage,
  onInfo,
  onRemove,
  onToggle,
  dataId,
  disabled,
  favoriteOnClick,
  appearance,
}) => {
  const hasSegment = Boolean(segment);
  const error = get(segment, 'error');
  const segmentClass = error ? 'segment-error' : `segment${slot + 1}`;
  const segmentsPopupActivator = (
    <Popover
      key="segment-activator"
      position="bottom"
      speechBubble
      noPadding
      focusOrder={['content']}
      disabled={disabled}
      activatorElement={(
        hasSegment
          ? (
            <Tooltip content={segment.name}>
              <Button
                key={`name-${withAction}-${withInfo}`
                  /* Fixes animation issue when remove button is being shown/hidden */
                }
                className={bindClasses(
                  'segment',
                  segmentClass,
                  'full-length',
                  appearance,
                  {
                    'with-action': withAction,
                    'with-info': withInfo,
                  },
                )}
                text={segment.name}
                cutText={withAction}
                onClick={onToggle}
                noHover
                dataId={`${dataId}.name`}
                disabled={disabled}
              />
            </Tooltip>
          )
          : (
            <Button
              icon="plus"
              className={bindClasses('segment', 'add', 'full-length', appearance)}
              noHover
              onClick={onToggle}
              dataId={`${dataId}.addSegment`}
              disabled={disabled}
            />
          )
      )}
      childToggle={{
        onItemSelected: () => true,
        onClick: () => true,
      }}
      dataId={dataId}
    >
      <SegmentPickerInner
        current={segment}
        onItemSelected={onItemSelected}
        items={items}
        isLoading={isLoading}
        onClick={onManage}
        canManage={canManage}
        dataId={`${dataId}.options`}
        favoriteOnClick={favoriteOnClick}
      />
    </Popover>
  );

  return (
    <div data-ppuc-id={dataId} className={bindClasses('wrapper')}>
      <div className={bindClasses('content', appearance)}>
        <ButtonGroup
          segmented
          className={
            bindClasses('no-break', { 'compare-mode': compareMode, 'segment-picker-sidebar': appearance === 'sidebar' })
          }
        >
          {hasSegment
            ? [
              segmentsPopupActivator,
              withInfo && (
                <Button
                  key="info"
                  className={bindClasses('segment', segmentClass)}
                  icon="info-circle"
                  onClick={() => { onInfo(segment); }}
                  noHover
                  dataId={`${dataId}.info`}
                  disabled={disabled}
                />
              ),
              withAction && (
                <Button
                  key="remove"
                  className={bindClasses('segment', segmentClass)}
                  icon="cross"
                  onClick={onRemove}
                  noHover
                  dataId={`${dataId}.remove`}
                  disabled={disabled}
                />
              ),
            ]
            : segmentsPopupActivator
          }
        </ButtonGroup>
        {Boolean(error) && <span className={bindClasses('message')}>{error}</span>}
      </div>
    </div>
  );
};

SegmentButton.defaultProps = {
  segment: undefined,
  withAction: false,
  withInfo: false,
  onRemove: null,
  dataId: 'SegmentButton',
  onInfo: null,
};

export default SegmentButton;
